import React, { useState, useEffect } from 'react';
import { Icon, CopyButton, CodeToken, Select, SelectItem } from '../components/ui';
import AupPanel from '../components/AupPanel';
import DbHealthPanel from '../components/DbHealthPanel';
import { Trans, useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface SettingsProps {
  apiUrl: string;
}

interface Agent {
  agent_id: string;
  name: string;
  role: string;
  status: string;
  created_at: string;
}

interface Token {
  token: string;
  expires_at: string;
  algorithm?: string;
  pqc?: boolean;
}

type PqcMode = 'off' | 'on' | 'required';

const Settings: React.FC<SettingsProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [apiHealth, setApiHealth] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Agent Registration State
  const [agentName, setAgentName] = useState('');
  const [agentRole, setAgentRole] = useState('developer');
  const [registeredAgent, setRegisteredAgent] = useState<Agent | null>(null);
  const [registrationLoading, setRegistrationLoading] = useState(false);

  // Token Generation State
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [generatedToken, setGeneratedToken] = useState<Token | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  // Post-Quantum Cryptography (PQC) State
  const [pqcMode, setPqcMode] = useState<PqcMode>('off');
  const [pqcConfigured, setPqcConfigured] = useState(false);
  const [pqcSaving, setPqcSaving] = useState(false);
  const [pqcAgentId, setPqcAgentId] = useState('');
  const [pqcAgentOverride, setPqcAgentOverride] = useState<string>('');
  const [pqcAgentEffective, setPqcAgentEffective] = useState<string>('');

  useEffect(() => {
    checkHealth();
    fetchActiveAgents();
    fetchPqcSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const checkHealth = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/health`);
      const data = await response.json();
      setApiHealth(data);
    } catch (error) {
      setApiHealth({ error: String(error) });
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveAgents = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/agents?status=active`);
      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    }
  };

  // ============================================================
  // AGENT REGISTRATION (Self-Service)
  // ============================================================
  const handleRegisterAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRegisteredAgent(null);

    if (!agentName.trim()) {
      setError('Agent name is required');
      return;
    }

    try {
      setRegistrationLoading(true);
      const response = await fetch(`${apiUrl}/agents/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: agentName,
          role: agentRole
        })
      });

      if (response.ok) {
        const data = await response.json();
        setRegisteredAgent(data as Agent);
        setAgentName('');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to register agent');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Error registering agent');
    } finally {
      setRegistrationLoading(false);
    }
  };

  // ============================================================
  // TOKEN GENERATION (Admin)
  // ============================================================
  const handleGenerateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGeneratedToken(null);

    if (!selectedAgentId) {
      setError('Please select an agent');
      return;
    }

    try {
      setTokenLoading(true);
      const response = await fetch(`${apiUrl}/admin/tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_id: selectedAgentId,
          expires_in_days: 30
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedToken(data as Token);
        setSelectedAgentId('');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to generate token');
      }
    } catch (error) {
      console.error('Token generation error:', error);
      setError('Error generating token');
    } finally {
      setTokenLoading(false);
    }
  };

  // ============================================================
  // POST-QUANTUM CRYPTOGRAPHY (PQC)
  // ============================================================
  const fetchPqcSettings = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/settings/pqc`);
      if (response.ok) {
        const data = await response.json();
        setPqcMode(data.mode as PqcMode);
        setPqcConfigured(Boolean(data.pqc_configured));
      }
    } catch (error) {
      console.error('Failed to fetch PQC settings:', error);
    }
  };

  const savePqcMode = async (mode: PqcMode) => {
    setError(null);
    setPqcSaving(true);
    try {
      const response = await fetch(`${apiUrl}/admin/settings/pqc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode })
      });
      if (response.ok) {
        const data = await response.json();
        setPqcMode(data.mode as PqcMode);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update PQC mode');
      }
    } catch (error) {
      setError('Error updating PQC mode');
    } finally {
      setPqcSaving(false);
    }
  };

  const fetchAgentPqc = async (agentId: string) => {
    if (!agentId) {
      setPqcAgentOverride('');
      setPqcAgentEffective('');
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/admin/agents/${agentId}/features/pqc`);
      if (response.ok) {
        const data = await response.json();
        setPqcAgentOverride(data.override || '');
        setPqcAgentEffective(data.effective || '');
      }
    } catch (error) {
      console.error('Failed to fetch agent PQC override:', error);
    }
  };

  const saveAgentPqc = async (mode: string) => {
    if (!pqcAgentId) return;
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/admin/agents/${pqcAgentId}/features/pqc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // empty string clears the override (falls back to platform default)
        body: JSON.stringify({ mode: mode === '' ? null : mode })
      });
      if (response.ok) {
        const data = await response.json();
        setPqcAgentOverride(data.override || '');
        setPqcAgentEffective(data.effective || '');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update agent PQC override');
      }
    } catch (error) {
      setError('Error updating agent PQC override');
    }
  };

  return (
    <div className="page-content">
      <h1>
        <Icon name="settings" size={20} /> {t('pages.settings.settingsAdministration')}
      </h1>

      {/* Onboarding Portal Banner */}
      <div className="notice info" style={{ marginTop: 18 }}>
        <Icon name="bolt" />
        <div>
          <strong>{t('pages.settings.newSelfServiceAgentOnboarding')}</strong>
          <div style={{ fontSize: 12.5, color: '#ffffff', marginTop: 2 }}>
            {t('pages.settings.useTheNewOnboardingPortalFor')}
          </div>
        </div>
        <a href="/agent-portal" target="_blank" rel="noopener noreferrer" className="ent-btn primary spacer">
          {t('pages.settings.goToPortal')}
          <Icon name="chevronRight" />
        </a>
      </div>

      {error && (
        <div className="notice error">
          <Icon name="warning" />
          {error}
        </div>
      )}

      {/* API Connection Section */}
      <div className="settings-section">
        <h2>
          <Icon name="signal" size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--text-2)' }} />
          {t('pages.settings.apiConnection')}
        </h2>
        <p>
          {t('pages.settings.currentAPIURL')} <code>{apiUrl}</code>
        </p>
        <button className="ent-btn" onClick={checkHealth} disabled={loading}>
          <Icon name="refresh" className={loading ? 'spin' : ''} />
          {loading ? t('pages.settings.checking') : t('pages.settings.checkAPIHealth')}
        </button>
        {apiHealth && (
          <pre style={{ padding: 14, marginTop: 12, overflow: 'auto', maxHeight: 200 }}>
            {JSON.stringify(apiHealth, null, 2)}
          </pre>
        )}
      </div>

      {/* Agent Registration Section */}
      <div className="settings-section">
        <h2>
          <Icon name="clipboardList" size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--text-2)' }} />
          {t('pages.settings.agentRegistrationSelfService')}
        </h2>
        <p>{t('pages.settings.registerANewAgentToGet')}</p>

        <form onSubmit={handleRegisterAgent} style={{ marginTop: 16 }}>
          <div className="ent-field">
            <label>{t('pages.settings.agentName')}</label>
            <input
              className="ent-input"
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder={t('pages.settings.eGMyDataAnalyzer')}
              required
            />
          </div>

          <div className="ent-field">
            <label>{t('pages.settings.role')}</label>
            <Select value={agentRole} onValueChange={setAgentRole}>
              <SelectItem value="viewer">{t('pages.settings.viewerReadOnlyAccess')}</SelectItem>
              <SelectItem value="developer">{t('pages.settings.developerReadWriteAccess')}</SelectItem>
              <SelectItem value="admin">{t('pages.settings.adminFullAccess')}</SelectItem>
            </Select>
          </div>

          <button type="submit" className="ent-btn primary" disabled={registrationLoading}>
            <Icon name="plus" />
            {registrationLoading ? t('pages.settings.registering') : t('pages.settings.registerAgent')}
          </button>
        </form>

        {registeredAgent && (
          <div className="notice success" style={{ marginTop: 16, alignItems: 'flex-start' }}>
            <Icon name="checkCircle" />
            <div>
              <strong>{t('pages.settings.agentRegistered')}</strong>
              <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-2)' }}>
                {t('pages.settings.agentIdLabel')}{' '}
                <span className="copy-cell">
                  <CodeToken>{registeredAgent.agent_id}</CodeToken>
                  <CopyButton value={registeredAgent.agent_id} title={t('pages.settings.copyAgentID')} />
                </span>
              </div>
              <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--text-2)' }}>
                {t('pages.settings.anAdministratorMustApproveYourRegistration')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Token Generation Section (Admin) */}
      <div className="settings-section">
        <h2>
          <Icon name="key" size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--text-2)' }} />
          {t('pages.settings.generateTokenAdminOnly')}
        </h2>
        <p>{t('pages.settings.generateANewJWTTokenFor')}</p>

        <form onSubmit={handleGenerateToken} style={{ marginTop: 16 }}>
          <div className="ent-field">
            <label>{t('pages.settings.selectActiveAgent')}</label>
            <Select
              value={selectedAgentId}
              onValueChange={setSelectedAgentId}
              placeholder={t('pages.settings.chooseAnAgent')}
            >
              {agents.map((agent) => (
                <SelectItem key={agent.agent_id} value={agent.agent_id}>
                  {agent.name} ({agent.role})
                </SelectItem>
              ))}
            </Select>
          </div>

          <button type="submit" className="ent-btn primary" disabled={tokenLoading || agents.length === 0}>
            <Icon name="key" />
            {tokenLoading ? t('pages.settings.generating') : t('pages.settings.generateToken')}
          </button>

          {agents.length === 0 && (
            <p style={{ marginTop: 8, fontSize: 13, color: 'var(--text-3)' }}>
              {t('pages.settings.noActiveAgentsAvailableApprovePending')}
            </p>
          )}
        </form>

        {generatedToken && (
          <div className="notice info" style={{ marginTop: 16, alignItems: 'flex-start' }}>
            <Icon name="checkCircle" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <strong>{t('pages.settings.tokenGenerated')}</strong>
              <div style={{ margin: '6px 0', fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>{t('pages.settings.expires', { v0: parseServerDate(generatedToken.expires_at).toLocaleString() })}</span>
                {generatedToken.algorithm && (
                  <span className={`badge ${generatedToken.pqc ? 'badge-success' : 'badge-info'}`}>
                    <Icon name="lock" size={12} /> {generatedToken.algorithm}
                    {generatedToken.pqc ? ` (${t('pages.settings.postQuantum')})` : ''}
                  </span>
                )}
              </div>
              <div className="code-editor" style={{ marginTop: 8 }}>
                <div className="code-editor-bar">
                  <span className="dot" style={{ background: 'var(--danger)' }} />
                  <span className="dot" style={{ background: 'var(--warning)' }} />
                  <span className="dot" style={{ background: 'var(--success)' }} />
                  <span style={{ marginLeft: 6 }}>{t('pages.settings.tokenJwt')}</span>
                  <span className="spacer" style={{ marginLeft: 'auto' }}>
                    <CopyButton value={generatedToken.token} title={t('pages.settings.copyToken')} />
                  </span>
                </div>
                <div
                  style={{
                    padding: '12px 14px',
                    fontFamily: 'var(--mono)',
                    fontSize: 11,
                    color: 'var(--text)',
                    wordBreak: 'break-all',
                    maxHeight: 110,
                    overflow: 'auto'
                  }}
                >
                  {generatedToken.token}
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="warning" size={14} />
                {t('pages.settings.saveThisTokenSecurelyYouWont')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Post-Quantum Cryptography Section */}
      <div className="settings-section">
        <h2>
          <Icon name="lock" size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--text-2)' }} />
          {t('pages.settings.postQuantumCryptographyPQC')}
        </h2>
        <p>
          <Trans
            i18nKey="pages.settings.pqcIntro"
            values={{ algorithm: t('pages.settings.mlDSA65NISTFIPS204') }}
            components={{ s: <strong /> }}
          />
        </p>

        {!pqcConfigured && (
          <div className="notice warning" style={{ marginTop: 12 }}>
            <Icon name="warning" />
            <div style={{ fontSize: 12.5 }}>
              <Trans i18nKey="pages.settings.pqcKeysNotConfigured" components={{ c: <code /> }} />
            </div>
          </div>
        )}

        {/* Platform-wide default */}
        <div className="ent-field" style={{ marginTop: 16 }}>
          <label>{t('pages.settings.platformDefaultMode')}</label>
          <Select
            value={pqcMode}
            onValueChange={(v) => savePqcMode(v as PqcMode)}
            disabled={pqcSaving}
          >
            <SelectItem value="off">{t('pages.settings.offClassicalHS256TokensOnly')}</SelectItem>
            <SelectItem value="on" disabled={!pqcConfigured}>{t('pages.settings.onIssueMLDSA65Accept')}</SelectItem>
            <SelectItem value="required" disabled={!pqcConfigured}>{t('pages.settings.requiredMLDSA65OnlyReject')}</SelectItem>
          </Select>
          <span style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
            {t('pages.settings.currentPlatformMode')}{' '}
            <span className={`badge ${pqcMode === 'off' ? 'badge-info' : 'badge-success'}`}>{pqcMode}</span>
          </span>
        </div>

        {/* Per-agent override */}
        <div style={{ marginTop: 20, padding: 14, background: 'var(--bg-2)', borderRadius: 6, borderLeft: '3px solid var(--info)' }}>
          <strong style={{ fontSize: 13 }}>{t('pages.settings.perAgentOverride')}</strong>
          <p style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 4 }}>
            {t('pages.settings.putASpecificHighAssuranceAgent')}
          </p>
          <div className="ent-field" style={{ marginTop: 10 }}>
            <label>{t('pages.settings.agent')}</label>
            <Select
              value={pqcAgentId}
              onValueChange={(v) => {
                setPqcAgentId(v);
                fetchAgentPqc(v);
              }}
              placeholder={t('pages.settings.chooseAnAgent')}
            >
              {agents.map((agent) => (
                <SelectItem key={agent.agent_id} value={agent.agent_id}>
                  {agent.name} ({agent.role})
                </SelectItem>
              ))}
            </Select>
          </div>
          {pqcAgentId && (
            <div className="ent-field">
              <label>{t('pages.settings.overrideMode')}</label>
              <Select
                value={pqcAgentOverride}
                onValueChange={(v) => saveAgentPqc(v)}
                placeholder={t('pages.settings.inheritPlatformDefault')}
              >
                <SelectItem value="off">{t('pages.settings.off')}</SelectItem>
                <SelectItem value="on" disabled={!pqcConfigured}>{t('pages.settings.on')}</SelectItem>
                <SelectItem value="required" disabled={!pqcConfigured}>{t('pages.settings.requiredHighAssurance')}</SelectItem>
              </Select>
              <span style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                {t('pages.settings.effectiveModeForAgent')}{' '}
                <span className={`badge ${pqcAgentEffective === 'off' ? 'badge-info' : 'badge-success'}`}>
                  {pqcAgentEffective || '—'}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* IDM Configuration Section */}
      <div className="settings-section">
        <h2>
          <Icon name="key" size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--text-2)' }} />
          {t('pages.settings.identityAccessManagementIDM')}
        </h2>
        <p>{t('pages.settings.integrationWithEnterpriseIdentityProvidersFor')}</p>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16, fontSize: 13 }}>
            <div>
              <strong style={{ color: 'var(--text-2)' }}>{t('pages.settings.idmProvider')}</strong>
            </div>
            <div>
              <CodeToken>{t('pages.settings.mock')}</CodeToken>
              <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 8 }}>
                {t('pages.settings.switchToActiveDirectoryEntraId')}
              </span>
            </div>

            <div>
              <strong style={{ color: 'var(--text-2)' }}>{t('pages.settings.serviceAccountCreation')}</strong>
            </div>
            <div>
              <span className="badge badge-success">
                <Icon name="checkBadge" size={12} /> {t('pages.settings.automaticOnApproval')}
              </span>
            </div>

            <div>
              <strong style={{ color: 'var(--text-2)' }}>{t('pages.settings.tokenExchange')}</strong>
            </div>
            <div>
              <CodeToken>{t('pages.settings.authToken')}</CodeToken>
              <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 8 }}>
                {t('pages.settings.credentialBasedJWTGeneration15mAccess')}
              </span>
            </div>

            <div>
              <strong style={{ color: 'var(--text-2)' }}>{t('pages.settings.authAttemptCache')}</strong>
            </div>
            <div>
              <span className="badge badge-info">
                <Icon name="refresh" size={12} /> {t('pages.settings.1MinuteTTL')}
              </span>
              <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 8 }}>
                {t('pages.settings.reducesIDMLoadPreventsBruteForce')}
              </span>
            </div>

            <div>
              <strong style={{ color: 'var(--text-2)' }}>{t('pages.settings.credentialEncryption')}</strong>
            </div>
            <div>
              <span className="badge badge-success">
                <Icon name="lock" size={12} /> {t('pages.settings.aes256GCM')}
              </span>
              <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 8 }}>
                {t('pages.settings.atRestEncryptionInDatabase')}
              </span>
            </div>

            <div>
              <strong style={{ color: 'var(--text-2)' }}>{t('pages.settings.degradedMode')}</strong>
            </div>
            <div>
              <span className="badge badge-warning">
                <Icon name="warning" size={12} /> {t('pages.settings.circuitBreaker')}
              </span>
              <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 8 }}>
                {t('pages.settings.issues15MinTokensIfIDM')}
              </span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20, padding: 12, background: 'var(--bg-2)', borderRadius: 6, borderLeft: '3px solid var(--info)' }}>
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            <strong>{t('pages.settings.dynamicTokenEndpoint')}</strong>
            <div style={{ marginTop: 8, fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text)' }}>
              POST /auth/token<br />
              {'{'}agentId, secret{'}'} → {'{'}accessToken, refreshToken, expiresIn{'}'}
            </div>
          </div>
        </div>
      </div>

      {/* System Information Section */}
      <div className="settings-section">
        <h2>
          <Icon name="info" size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--text-2)' }} />
          {t('pages.settings.systemInformation')}
        </h2>
        <ul style={{ marginTop: 10 }}>
          <li><strong>{t('pages.settings.agenti5Version')}</strong> {t('pages.settings.100MVPWithIDM')}</li>
          <li><strong>{t('pages.settings.dashboardVersion')}</strong> 1.0.0</li>
          <li><strong>{t('pages.settings.firewall')}</strong> {t('pages.settings.agentBasedWithJWTIDMAuth')}</li>
          <li><strong>{t('pages.settings.policies')}</strong> {t('pages.settings.dynamicDatabaseDriven')}</li>
          <li><strong>{t('pages.settings.idmIntegration')}</strong> {t('pages.settings.activeServiceAccountProvisioning')}</li>
          <li><strong>{t('pages.settings.tokenTypes')}</strong> {t('pages.settings.longLivedLegacyDynamic15m24h')}</li>
          <li><strong>{t('pages.settings.rateLimiting')}</strong> {t('pages.settings.10ReqMinPerAgent')}</li>
          <li><strong>{t('pages.settings.piiDetection')}</strong> {t('pages.settings.enabledSSNEmailCreditCardEtc')}</li>
          <li><strong>{t('pages.settings.credentialEncryption2')}</strong> {t('pages.settings.aes256GCMAtRestQuantum')}</li>
          <li><strong>{t('pages.settings.postQuantumTokens')}</strong> {pqcConfigured ? t('pages.settings.pqcMode', { mode: pqcMode }) : t('pages.settings.pqcModeNotConfigured', { mode: pqcMode })}</li>
          <li><strong>{t('pages.settings.nodeEnvironment')}</strong> {process.env.NODE_ENV || t('pages.settings.development')}</li>
          <li><strong>{t('pages.settings.buildDate')}</strong> {new Date().toLocaleDateString()}</li>
        </ul>
      </div>

      {/* Database health & retention */}
      <div className="settings-section">
        <h2>
          <Icon name="info" size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--text-2)' }} />
          {t('pages.settings.databaseHealthRetention')}
        </h2>
        <DbHealthPanel apiUrl={apiUrl} />
      </div>

      {/* Acceptable Use Policy */}
      <div className="settings-section">
        <h2>
          <Icon name="document" size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--text-2)' }} />
          {t('pages.settings.acceptableUsePolicy')}
        </h2>
        <AupPanel apiUrl={apiUrl} />
      </div>

      {/* Documentation Section */}
      <div className="settings-section">
        <h2>
          <Icon name="document" size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--text-2)' }} />
          {t('pages.settings.documentation')}
        </h2>
        <ul>
          <li><strong>{t('pages.settings.gettingStarted')}</strong> {t('pages.settings.registerAnAgentAboveWaitFor')}</li>
          <li><strong>{t('pages.settings.legacyAuthorization')}</strong> {t('pages.settings.includeTokenInRequestsAs')} <code>Authorization: Bearer {'{token}'}</code></li>
          <li><strong>{t('pages.settings.dynamicTokenExchange')}</strong> {t('pages.settings.use')} <code>POST /auth/token</code> {t('pages.settings.withAgentIdIDMSecretToGet')}</li>
          <li><strong>{t('pages.settings.tokenRefresh')}</strong> {t('pages.settings.use')} <code>POST /auth/refresh</code> {t('pages.settings.withRefreshTokenToGetNew')}</li>
          <li><strong>{t('pages.settings.policies')}</strong> {t('pages.settings.goToPolicyManagementToControl')}</li>
          <li><strong>{t('pages.settings.agents')}</strong> {t('pages.settings.goToAgentManagementToApprove')}</li>
          <li><strong>{t('pages.settings.idmStatus')}</strong> {t('pages.settings.checkTheIDMSectionAboveOr')}</li>
        </ul>
      </div>
    </div>
  );
};

export default Settings;
